import styled from '@emotion/styled'
import { Close } from 'app/components/Common/Close'
import { styles, theme } from 'app/theme'
import useLockedBody from 'app/utils/useLockedBody'
import { useVocabularyData } from 'app/utils/vocabulary'
import { rgba } from 'emotion-rgba'
import React, { memo, useContext, useEffect, useRef, useState } from 'react'

import { FooterContext } from './index'

export const CompanyData = memo(function CompanyData() {
  const {
    administrationAddress,
    administrationEmail,
    administrationPhone,
    businessName,
    cin,
    languageCode,
    legalRepresentative,
    pec,
    rea,
    shareCapital,
    vat,
  } = useContext(FooterContext) as any

  const mainRef = useRef<any>(null)

  const [_locked, setLocked] = useLockedBody()
  const [modalStatus, setModalStatus] = useState(false)

  useEffect(() => {
    if (modalStatus) {
      mainRef.current.focus()
    }

    document.addEventListener('keydown', function (e) {
      switch (e.key) {
        case 'Escape':
          setLocked(false)
          setModalStatus(false)
          break
      }
    })
  }, [modalStatus])

  return (
    <>
      <Button
        onClick={() => {
          setLocked(true)
          setModalStatus(true)
        }}
        onKeyDown={(e: any) => {
          if (e.key === 'Enter') {
            setLocked(true)
            setModalStatus(true)
          }
        }}
        role="button"
        tabIndex={0}
      >
        {useVocabularyData('company-data', languageCode)}
      </Button>

      <Modal
        aria-hidden={!modalStatus}
        aria-labelledby="Company Data"
        className={modalStatus ? ' open' : undefined}
        data-lenis-prevent
        role="dialog"
      >
        <Close
          languageCode={languageCode}
          onClick={() => {
            setLocked(false)
            setModalStatus(false)
          }}
        />

        <Title>{useVocabularyData('company-data', languageCode)}</Title>

        <Data ref={mainRef} tabIndex={-1}>
          {pec ? (
            <Row>
              <Label>{useVocabularyData('pec', languageCode)}</Label>

              <Anchor href={`mailto:${pec}`}>{pec}</Anchor>
            </Row>
          ) : null}

          {administrationEmail ? (
            <Row>
              <Label>
                {useVocabularyData('registered-office-email', languageCode)}
              </Label>

              <Anchor href={`mailto:${administrationEmail}`}>
                {administrationEmail}
              </Anchor>
            </Row>
          ) : null}

          {administrationPhone ? (
            <Row>
              <Label>
                {useVocabularyData('registered-office-phone', languageCode)}
              </Label>

              <Anchor href={`tel:${administrationPhone}`}>
                {administrationPhone}
              </Anchor>
            </Row>
          ) : null}

          {businessName ? (
            <Row>
              <Label>{useVocabularyData('business-name', languageCode)}</Label>

              <Item>{businessName}</Item>
            </Row>
          ) : null}

          {legalRepresentative ? (
            <Row>
              <Label>
                {useVocabularyData('legal-representative', languageCode)}
              </Label>

              <Item>{legalRepresentative}</Item>
            </Row>
          ) : null}

          {administrationAddress ? (
            <Row>
              <Label>
                {useVocabularyData('registered-office-address', languageCode)}
              </Label>

              <Item>{administrationAddress}</Item>
            </Row>
          ) : null}

          {vat ? (
            <Row>
              <Label>{useVocabularyData('vat', languageCode)}</Label>

              <Item>{vat}</Item>
            </Row>
          ) : null}

          {rea ? (
            <Row>
              <Label>{useVocabularyData('rea', languageCode)}</Label>

              <Item>{rea}</Item>
            </Row>
          ) : null}

          {shareCapital ? (
            <Row>
              <Label>{useVocabularyData('share-capital', languageCode)}</Label>

              <Item>{shareCapital}</Item>
            </Row>
          ) : null}

          {cin ? (
            <Row>
              <Label>{useVocabularyData('cin', languageCode)}</Label>

              <Item>{cin}</Item>
            </Row>
          ) : null}
        </Data>
      </Modal>
    </>
  )
})

const Button = styled.div`
  ${styles.description}

  color: ${({ theme }) => theme.colors.variants.neutralLight1};
  cursor: pointer;
  font-size: 0.875rem;
  line-height: 1.25rem;
  position: relative;
  &:after {
    content: '';
    width: 100%;
    height: 0.0625rem;
    background: ${({ theme }) => theme.colors.variants.neutralLight1};
    pointer-events: none;
    position: absolute;
    bottom: 0;
    left: 0;
    transform: scaleX(0);
    transform-origin: right;
    transition: transform 0.8s cubic-bezier(0.24, 0.43, 0.15, 0.97);
  }
  &:hover {
    &:after {
      transform: scaleX(1);
      transform-origin: left;
    }
  }
`

const Modal = styled.div`
  width: 50%;
  height: 100%;
  background: ${({ theme }) => theme.colors.variants.neutralLight3};
  box-shadow: -1.875rem 0rem 1.875rem 1.875rem
    ${rgba(theme.colors.variants.neutralDark1, 0.05)};
  overflow-y: auto;
  padding: 7.375rem 6.667vw;
  pointer-events: none;
  position: fixed;
  bottom: 0;
  right: 0;
  z-index: 10001;
  transform: translateX(100%);
  transition: 1.1s cubic-bezier(0.87, 0, 0.13, 1);
  visibility: hidden;
  -webkit-overflow-scrolling: auto;
  &.open {
    pointer-events: unset;
    transform: none;
    visibility: visible;
  }

  @media (max-width: 1199px) {
    width: 100%;
    padding: 7.5rem 2rem;
  }
`

const Title = styled.div`
  ${styles.title.small}
`

const Data = styled.div`
  margin-top: 2rem;
`

const Row = styled.div`
  border-bottom: 0.0625rem solid
    ${rgba(theme.colors.variants.neutralDark1, 0.1)};
  color: ${({ theme }) => theme.colors.variants.primaryDark};
  padding: 1.125rem 0 0.8125rem;
  &:first-of-type {
    padding-top: 0;
  }
  &:last-of-type {
    border: 0;
    padding-bottom: 0;
  }
`

const Label = styled.div`
  ${styles.label}

  margin-bottom: 0.25rem;

  @media (max-width: 1199px) {
    margin-bottom: 0.25rem;
  }
`

const Anchor = styled.a`
  ${styles.description}

  transition: .2s ease-in-out;
  &:hover {
    opacity: 0.5;
  }
`

const Item = styled.div`
  ${styles.description}
`
